import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import { User as UserIcon, Envelope, Phone, ShieldCheck, IdentificationBadge, MapPin, BookOpen, Info, ArrowLeft } from "@phosphor-icons/react";

const Field = ({ icon: Icon, label, value }) => (
  <div className="bg-[#0B1A3A] border border-primary/15 rounded-xl p-4 flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/25 flex items-center justify-center flex-shrink-0">
      <Icon size={18} weight="duotone" className="text-primary" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-white/50 text-xs">{label}</div>
      <div className="text-white font-medium text-sm mt-0.5 truncate">{value || "—"}</div>
    </div>
  </div>
);

export default function Account() {
  const { user } = useAuth();
  const [me, setMe] = useState(user);
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";

  useEffect(() => { setMe(user); }, [user]);

  const isAdmin = !!(me?.is_admin || me?.role === "admin");
  const isDriver = me?.role === "driver";

  return (
    <div className="min-h-screen bg-background text-foreground" dir={isEn ? "ltr" : "rtl"}>
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Hero */}
        <div className="bg-card border border-primary/25 rounded-2xl p-6 flex items-center gap-5 mb-6 gold-glow">
          <div className="w-20 h-20 rounded-full flex items-center justify-center border-2 border-primary/40 bg-primary/10">
            {isAdmin ? <Logo variant="mark" size={48} /> : <UserIcon size={36} weight="duotone" className="text-primary" />}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{me?.name || "—"}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {isAdmin && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs">
                  <ShieldCheck size={12} weight="fill" /> {me?.role_ar || t("account.adminBadge")}
                </span>
              )}
              {isDriver && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-[#2E7D32]/50 bg-[#2E7D32]/10 text-[#2E7D32] text-xs">
                  {t("account.driverBadge")}
                </span>
              )}
              {!isAdmin && !isDriver && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-white/20 bg-white/5 text-white/70 text-xs">
                  {t("account.companyBadge")}
                </span>
              )}
              <span className="text-white/40 text-xs">·</span>
              <span className="text-white/50 text-xs">{t("account.subline")}</span>
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="account-details">
          <Field icon={Envelope} label={t("account.email")} value={me?.email} />
          <Field icon={Phone} label={t("account.phone")} value={me?.phone ? formatPhoneDisplay(me.phone) : ""} />
          {me?.commercial_register_number && (
            <Field icon={IdentificationBadge} label={t("account.cr")} value={me.commercial_register_number} />
          )}
          {me?.address && <Field icon={MapPin} label={t("account.address")} value={me.address} />}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to={isAdmin ? "/admin/help" : isDriver ? "/driver/help" : "/help"}
            data-testid="account-help-link"
            className="inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-primary/8 border border-primary/30 text-primary hover:bg-primary/15 transition-all font-bold text-sm"
          >
            <span className="inline-flex items-center gap-2">
              <BookOpen size={16} weight="duotone" /> {t("account.helpGuide")}
            </span>
            <ArrowLeft size={14} weight="bold" className={isEn ? "" : "rotate-180"} />
          </Link>
          <Link
            to="/about"
            data-testid="account-about-link"
            className="inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-primary/8 border border-primary/30 text-primary hover:bg-primary/15 transition-all font-bold text-sm"
          >
            <span className="inline-flex items-center gap-2">
              <Info size={16} weight="duotone" /> {t("account.aboutCompany")}
            </span>
            <ArrowLeft size={14} weight="bold" className={isEn ? "" : "rotate-180"} />
          </Link>
        </div>

        <div className="mt-6 text-center text-white/40 text-xs">
          {t("account.editNote")}
        </div>
      </main>
    </div>
  );
}
